import React, { ReactNode, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/classes";
import { TabsContext } from "./tabs-context";
import { Tab } from "./tab";
import { TabPanel } from "./tab-panel";

/* Variants */
const tabsVariants = cva("tabs", {
  variants: {
    variant: {
      lifted: "tabs-lift",
      bordered: "tabs-border",
      boxed: "tabs-box",
    },
    size: {
      small: "tabs-sm",
      medium: "",
      large: "tabs-lg",
    },
  },
  defaultVariants: {
    variant: "lifted",
    size: "medium",
  },
});

/* Prop Types */
export interface TabsProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof tabsVariants> {
  children: ReactNode;
  defaultTab?: string | number;
  value?: string | number;
  onTabChange?: (tab: string | number) => void;
  showActiveBg?: boolean;
}

/*
 * Tabs Component
 *
 * Wrapper for Tab and TabPanel components.
 */
export const Tabs = ({ children, defaultTab, value, onTabChange, showActiveBg, variant, size, className, ...props }: TabsProps) => {
  /* State */
  const [internalActiveTab, setInternalActiveTab] = useState<string | number>(defaultTab ?? "");

  /* Consts */
  const activeTab = value !== undefined ? value : internalActiveTab;

  /* Handlers */
  const handleTabChange = (tab: string | number) => {
    if (value === undefined) {
      setInternalActiveTab(tab);
    }
    onTabChange?.(tab);
  };

  // Separate tabs and panels, then reorder for DaisyUI structure
  const tabs: React.ReactElement[] = [];
  const panels: React.ReactElement[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === Tab) {
        tabs.push(child);
      } else if (child.type === TabPanel) {
        panels.push(child);
      }
    }
  });

  // DaisyUI requires content to follow tab due to css selectors.
  // .e.g. Tab1 -> Panel1 -> Tab2 -> Panel2 -> etc.
  // Below we create a new array with the correct order.
  const orderedElements: React.ReactElement[] = [];
  tabs.forEach((tab) => {
    orderedElements.push(tab);
    // Find matching panel by value
    const tabValue = (tab.props as { value: string | number })?.value;
    const matchingPanel = panels.find((panel) => {
      const panelValue = (panel.props as { value: string | number })?.value;
      return panelValue === tabValue;
    });
    if (matchingPanel) {
      orderedElements.push(matchingPanel);
    }
  });

  /* Return */
  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange, showActiveBg }}>
      <div {...props} className={cn(tabsVariants({ variant, size }), className)} role="tablist">
        {orderedElements}
      </div>
    </TabsContext.Provider>
  );
};
